import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  LogOut,
  LayoutDashboard,
  Bell,
  Heart,
  ShoppingBag,
  User,
  TicketCheck,
} from "lucide-react";

const fallbackAvatar =
  "https://static.vecteezy.com/system/resources/thumbnails/002/002/403/small/man-with-beard-avatar-character-isolated-icon-free-vector.jpg";

const MenuItem = ({ children, danger }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "9px 14px",
        background: hovered ? "#f9fafb" : "transparent",
        transition: "background 0.1s",
        cursor: "pointer",
        color: danger ? "#ef4444" : "#4b5563",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 6,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  );
};

export default function ProfileDropdown({
  userDetails,
  dashboardLink,
  onLogout,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ top: 68, right: 8, width: 190 });

  const wrapperRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = () => {
    const trigger = wrapperRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    setMenuStyle({
      top: rect.bottom + 6,
      right: Math.max(8, viewportWidth - rect.right),
      width: Math.min(190, viewportWidth - 16),
    });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !wrapperRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      )
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  const firstName = userDetails?.name?.split(" ")?.[0] || "User";
  const profileImage =
    userDetails?.userProfilePhoto?.length > 0
      ? userDetails?.userProfilePhoto?.[0]?.extraLarge?.imageUrl
      : fallbackAvatar;

  const divider = (
    <div style={{ height: 1, background: "#f3f4f6", margin: "4px 0" }} />
  );

  return (
    <div
      ref={wrapperRef}
      style={{ display: "flex", alignItems: "center" }}
      onMouseEnter={() => {
        updateMenuPosition();
        setIsOpen(true);
      }}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Trigger */}
      <button
        type="button"
        onClick={() => {
          if (!isOpen) updateMenuPosition();
          setIsOpen((p) => !p);
        }}
        aria-label="Profile menu"
        aria-expanded={isOpen}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <img
          src={profileImage}
          alt="User Profile"
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            objectFit: "cover",
            border: "2px solid #e5e7eb",
            display: "block",
          }}
        />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.13, ease: "easeOut" }}
            style={{
              position: "fixed",
              top: menuStyle.top,
              right: menuStyle.right,
              width: menuStyle.width,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              boxShadow: "0 8px 20px rgba(0,0,0,0.07)",
              zIndex: 1300,
              overflow: "hidden",
              padding: "4px 0",
            }}
          >
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              style={{ textDecoration: "none", display: "block" }}
            >
              <MenuItem>
                <User size={13} />
                <span style={{ color: "#111827", fontWeight: 600 }}>
                  Hi, {firstName}
                </span>
              </MenuItem>
            </Link>

            {divider}

            <a
              href={dashboardLink}
              target="_parent"
              onClick={() => setIsOpen(false)}
              style={{ textDecoration: "none", display: "block" }}
            >
              <MenuItem>
                <LayoutDashboard size={13} />
                Dashboard
              </MenuItem>
            </a>

            <Link
              to="/notifications"
              onClick={() => setIsOpen(false)}
              style={{ textDecoration: "none", display: "block" }}
            >
              <MenuItem>
                <Bell size={13} />
                Notification
              </MenuItem>
            </Link>

            <Link
              to="/my-wishes"
              onClick={() => setIsOpen(false)}
              style={{ textDecoration: "none", display: "block" }}
            >
              <MenuItem>
                <Heart size={13} />
                My Wishes
              </MenuItem>
            </Link>

            <Link
              to="/my-orders"
              onClick={() => setIsOpen(false)}
              style={{ textDecoration: "none", display: "block" }}
            >
              <MenuItem>
                <ShoppingBag size={13} />
                My Orders
              </MenuItem>
            </Link>

            {/* There is no ticketing module in the API — support runs through
                the contact page. */}
            <Link
              to="/contact-us"
              onClick={() => setIsOpen(false)}
              style={{ textDecoration: "none", display: "block" }}
            >
              <MenuItem>
                <TicketCheck size={13} />
                Contact Support
              </MenuItem>
            </Link>

            {divider}

            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              style={{
                width: "100%",
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <MenuItem danger>
                <LogOut size={13} />
                Logout
              </MenuItem>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
